/**
 * @fileoverview Overlay de Transmisión Cintillo OBS (/abcdef/overlays/quick-reaction)
 * para la dinámica "Reacción Rápida Personal" (HU-05).
 * 
 * Diseñado específicamente para producción broadcast en OBS / vMix / TriCaster con fondo transparente,
 * renderizado fluido a 60 FPS acelerado por GPU y sincronización en tiempo real vía SignalR.
 * 
 * Zero-Canvas Rule: No renderiza nada en estado idle.
 * Cero exposición de PII sensible (sin correos ni teléfonos).
 * 
 * @module components/Overlays/PersonalQuickReactionOverlay
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetPublicConfig, GetWinner } from '../../utilities/DL/PersonalQuickReaction';
import SignalRService from '../../utilities/WS/SignalRService';
import { PersonalQuickReaction as PersonalQuickReactionView } from './Overlays/PersonalQuickReaction';

/**
 * COMPONENTE: PERSONAL QUICK REACTION OVERLAY (PARENT CONTROLLER)
 * ------------------------------------------------------------------
 * Este componente es el contenedor y cerebro del overlay:
 * 1. Lee el roomId desde query params (?roomId=... o ?room=...) o props.
 * 2. Garantiza fondo transparente total en el DOM (body, html, root).
 * 3. Consulta la configuración inicial pública y el ganador actual vía HTTP REST (DL).
 * 4. Establece conexión en tiempo real con SignalR y escucha los eventos de overlay,
 *    configuración, nuevas jugadas ganadoras y reinicios.
 * 5. Gestiona el ciclo de vida de animación (flowState: idle -> entering -> visible -> fadingOut -> idle).
 * 6. Pasa el estado y parámetros al componente visual <PersonalQuickReactionView />.
 * ------------------------------------------------------------------
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.roomId=''] - Identificador opcional de la sala.
 */
export function PersonalQuickReactionOverlay({ roomId: propRoomId = '' }) {
    // 1. Obtener roomId de props o query params
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const roomId = useMemo(() => {
        return propRoomId || searchParams.get('roomId') || searchParams.get('room') || '';
    }, [propRoomId, searchParams]);

    // 2. Estados de configuración visual
    const [offsetY, setOffsetY] = useState(0);
    const [banner1536Url, setBanner1536Url] = useState('');
    const [banner1920Url, setBanner1920Url] = useState('');
    const [qrCount, setQrCount] = useState(1);
    const [qrCenterLogoUrl, setQrCenterLogoUrl] = useState('');
    const [overlayMode, setOverlayMode] = useState('invitation'); // 'invitation' | 'winner'
    const [winnerData, setWinnerData] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState('idle');

    // 3. Control del flujo de visibilidad ('idle' | 'entering' | 'visible' | 'fadingOut')
    const [flowState, setFlowState] = useState('idle');

    // 4. Forzar fondo transparente total para integración OBS / vMix Browser Source
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');
        const originalRootBg = rootElement ? rootElement.style.backgroundColor : '';

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (rootElement) {
                rootElement.style.backgroundColor = originalRootBg;
            }
        };
    }, []);

    // 5. Control de transiciones de animación (entering -> visible, fadingOut -> idle)
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            timeout = setTimeout(() => setFlowState('visible'), 500);
        } else if (flowState === 'fadingOut') {
            timeout = setTimeout(() => {
                setFlowState('idle');
            }, 500);
        }
        return () => clearTimeout(timeout);
    }, [flowState]);

    const handleOverlayVisibilityChange = useCallback((visible) => {
        if (visible) {
            setFlowState((prev) => {
                if (prev === 'idle' || prev === 'fadingOut') {
                    return 'entering';
                }
                return prev;
            });
        } else {
            setFlowState((prev) => {
                if (prev !== 'idle' && prev !== 'fadingOut') {
                    return 'fadingOut';
                }
                return prev;
            });
        }
    }, []);

    // 6. Aplicar datos de configuración y overlay
    const applyOverlayData = useCallback((data) => {
        if (!data) return;

        if (data.overlayVisible !== undefined) {
            handleOverlayVisibilityChange(Boolean(data.overlayVisible));
        } else if (data.isOverlayVisible !== undefined) {
            handleOverlayVisibilityChange(Boolean(data.isOverlayVisible));
        }

        if (data.overlayMode) {
            setOverlayMode(String(data.overlayMode).toLowerCase());
        }

        if (data.overlayQrCount !== undefined) {
            setQrCount(Number(data.overlayQrCount) || 1);
        } else if (data.qrCount !== undefined) {
            setQrCount(Number(data.qrCount) || 1);
        }

        if (data.overlayOffsetY !== undefined) {
            setOffsetY(Number(data.overlayOffsetY) || 0);
        } else if (data.offsetY !== undefined) {
            setOffsetY(Number(data.offsetY) || 0);
        }

        const b1536 = data.overlayBanner1536Url !== undefined ? data.overlayBanner1536Url
            : data.banner1536Url !== undefined ? data.banner1536Url
            : data.bannerImageMdUrl;
        if (b1536 !== undefined) {
            setBanner1536Url(b1536 || '');
        }

        const b1920 = data.overlayBanner1920Url !== undefined ? data.overlayBanner1920Url
            : data.banner1920Url !== undefined ? data.banner1920Url
            : data.bannerImageLgUrl;
        if (b1920 !== undefined) {
            setBanner1920Url(b1920 || '');
        }

        if (data.qrCenterLogoUrl !== undefined) {
            setQrCenterLogoUrl(data.qrCenterLogoUrl || '');
        }
    }, [handleOverlayVisibilityChange]);

    // 7. Carga inicial de datos (REST) y conexión a SignalR en tiempo real
    useEffect(() => {
        let isMounted = true;

        const loadInitialData = async () => {
            try {
                // Cargar configuración pública (con o sin roomId explícito)
                const configRes = await GetPublicConfig(roomId || null);
                if (isMounted && configRes && configRes.success && configRes.data) {
                    applyOverlayData(configRes.data);
                    const isVisible = configRes.data.overlayVisible !== undefined
                        ? Boolean(configRes.data.overlayVisible)
                        : (configRes.data.isOverlayVisible !== undefined ? Boolean(configRes.data.isOverlayVisible) : false);
                    setFlowState(isVisible ? 'visible' : 'idle');
                }

                // Cargar ganador actual si existe
                const winnerRes = await GetWinner(roomId || null);
                if (isMounted && winnerRes && winnerRes.success && winnerRes.data) {
                    setWinnerData(winnerRes.data);
                }
            } catch (err) {
                console.warn('[PersonalQuickReactionOverlay] Error en carga inicial:', err);
            }

            // Conectar a SignalR Hub
            try {
                await SignalRService.connect({ roomId, tipo: 'overlay' });
                if (isMounted) setConnectionStatus('connected');
            } catch (err) {
                console.error('[PersonalQuickReactionOverlay] Error conectando a SignalR:', err);
                if (isMounted) setConnectionStatus('error');
            }
        };

        loadInitialData();

        // 8. Handlers de eventos en tiempo real vía SignalR
        const handleOverlayUpdated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            applyOverlayData(data);
        };

        const handleConfigUpdated = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            applyOverlayData(data);
        };

        const handleNewPlay = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;

            // Si la jugada es declarada ganadora o queda en primer lugar, actualizar ganador
            if (data.isWinner || data.rankingPosition === 1) {
                setWinnerData({
                    participantId: data.participantId,
                    participantName: data.participantName || '',
                    reactionTimeMs: data.reactionTimeMs || 0,
                    penaltyTimeMs: data.penaltyTimeMs || 0,
                    totalTimeMs: data.totalTimeMs !== undefined ? data.totalTimeMs : ((data.reactionTimeMs || 0) + (data.penaltyTimeMs || 0)),
                    earlyAttemptsCount: data.earlyAttemptsCount || 0,
                    badgeName: data.badgeName || '',
                    playedAtUtc: data.playedAtUtc
                });
            }
        };

        const handleReset = (payload) => {
            const data = payload && (payload.data !== undefined ? payload.data : payload);
            if (!data) return;

            if (data.resetType === 'FULL') {
                setWinnerData(null);
            } else if (roomId) {
                // Si fue reinicio parcial/usuario, recargar ganador oficial vigente
                GetWinner(roomId).then((res) => {
                    if (isMounted) {
                        if (res && res.success && res.data) {
                            setWinnerData(res.data);
                        } else {
                            setWinnerData(null);
                        }
                    }
                }).catch(() => {});
            }
        };

        const handleReconnected = () => {
            if (isMounted) {
                setConnectionStatus('connected');
                loadInitialData();
            }
        };

        // Suscripción a eventos de SignalR
        SignalRService.on('personalQuickReaction:overlayUpdated', handleOverlayUpdated);
        SignalRService.on('personalQuickReaction:configUpdated', handleConfigUpdated);
        SignalRService.on('personalQuickReaction:newPlay', handleNewPlay);
        SignalRService.on('personalQuickReaction:reset', handleReset);
        SignalRService.on('reconnected', handleReconnected);

        return () => {
            isMounted = false;
            SignalRService.off('personalQuickReaction:overlayUpdated', handleOverlayUpdated);
            SignalRService.off('personalQuickReaction:configUpdated', handleConfigUpdated);
            SignalRService.off('personalQuickReaction:newPlay', handleNewPlay);
            SignalRService.off('personalQuickReaction:reset', handleReset);
            SignalRService.off('reconnected', handleReconnected);
            SignalRService.disconnect();
        };
    }, [roomId, applyOverlayData]);

    return (
        <>
            <PersonalQuickReactionView
                flowState={flowState}
                overlayMode={overlayMode}
                qrCount={qrCount}
                offsetY={offsetY}
                banner1536Url={banner1536Url}
                banner1920Url={banner1920Url}
                qrCenterLogoUrl={qrCenterLogoUrl}
                winnerData={winnerData}
                roomId={roomId}
            />

            {connectionStatus === 'error' && (
                <div style={{
                    position: 'fixed',
                    bottom: 5,
                    right: 5,
                    background: 'red',
                    color: 'white',
                    padding: '3px 8px',
                    fontSize: '9px',
                    borderRadius: '4px',
                    opacity: 0.8,
                    zIndex: 99999
                }}>
                    D
                </div>
            )}
        </>
    );
}

PersonalQuickReactionOverlay.propTypes = {
    roomId: PropTypes.string
};

export default PersonalQuickReactionOverlay;
